/* RYŌHŌ — Barre d'ajout au panier collante (mobile).
   - Le clic relaie vers le VRAI bouton du formulaire produit : aucun second formulaire, donc
     tiroir panier, tracking et applications tierces empruntent le chemin de code habituel.
   - Le prix est resynchronisé si Dawn re-rend le bloc prix (changement de variante).

   Régression corrigée le 2026-08-02 — la barre était invisible pour tout visiteur n'ayant pas
   encore répondu à la bannière de consentement Shopify (#shopify-pc__banner, position:fixed,
   z-index:2000000) : celle-ci couvrait intégralement la barre (z-index:3) et interceptait les
   clics. Preuve : elementFromPoint au centre de la barre renvoyait le bouton « Gérer vos
   préférences ». On ne passe PAS devant la bannière (UI de consentement, intouchable) : la barre
   s'efface tant qu'elle est ouverte, et reparaît dès que la cliente a choisi.

   Second correctif : la barre ne s'affiche plus que lorsque le bouton principal est sorti par le
   HAUT (déjà dépassé), et non lorsqu'il est simplement hors écran plus bas — sinon elle
   s'affichait dès le chargement, avant même que la cliente ait vu le bouton réel. */

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // Idempotent : une reconnexion ne doit jamais empiler un 2e listener
        // (sinon this.target.click() partirait deux fois = double ajout au panier).
        this.teardown();
        this.target = document.getElementById(this.dataset.target);
        this.button = this.querySelector('[data-sticky-add]');
        if (!this.target || !this.button) return;

        this.scrolledPast = false;
        this.consentOpen = false;

        this.onClickBound = this.onClickBound || this.onClick.bind(this);
        this.button.removeEventListener('click', this.onClickBound);
        this.button.addEventListener('click', this.onClickBound);
        this.syncDisabled();

        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // isIntersecting=false ne suffit pas : le bouton est aussi « hors écran »
              // quand il est ENCORE PLUS BAS, au chargement. On ne montre la barre que
              // lorsqu'il est passé au-dessus du viewport.
              this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.render();
            });
          },
          { root: null, threshold: 0 }
        );
        this.io.observe(this.target);

        this.watchConsent();

        // Le bouton principal peut passer à « Épuisé »/disabled après un changement de variante.
        // Dawn signale « envoi en cours » via aria-disabled (product-form.js), pas disabled.
        this.targetObserver = new MutationObserver(this.syncDisabled.bind(this));
        this.targetObserver.observe(this.target, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });

        const priceSource = document.getElementById(this.dataset.priceSource);
        this.priceSlot = this.querySelector('[data-sticky-price]');
        if (priceSource && this.priceSlot) {
          this.priceObserver = new MutationObserver(() => this.syncPrice(priceSource));
          this.priceObserver.observe(priceSource, { childList: true, subtree: true });
        }
      }

      disconnectedCallback() {
        this.teardown();
      }

      teardown() {
        this.io?.disconnect();
        this.io = null;
        this.targetObserver?.disconnect();
        this.targetObserver = null;
        this.priceObserver?.disconnect();
        this.priceObserver = null;
        this.consentBodyObserver?.disconnect();
        this.consentBodyObserver = null;
        this.consentElObserver?.disconnect();
        this.consentElObserver = null;
        this.consentEl = null;
        // Le différé de visitorConsentCollected doit être annulé : sinon il se déclenche
        // après le démontage, consentEl vaut null, et check() recrée un MutationObserver
        // que plus personne ne déconnectera (fuite retenant un nœud détaché).
        if (this.consentTimer) {
          clearTimeout(this.consentTimer);
          this.consentTimer = null;
        }
        if (this.consentRaf) {
          cancelAnimationFrame(this.consentRaf);
          this.consentRaf = null;
        }
        if (this.onConsentCollected) {
          document.removeEventListener('visitorConsentCollected', this.onConsentCollected);
          this.onConsentCollected = null;
        }
      }

      /* Surveille la bannière de consentement Shopify sans jamais la modifier.
         Trois signaux, aucun minuteur permanent :
         - ajout/retrait du <section id="shopify-pc__banner"> (observé sur <body>) ;
         - masquage par attribut/classe/style sur la bannière elle-même ;
         - événement natif visitorConsentCollected émis au choix de la cliente. */
      watchConsent() {
        const check = () => {
          this.consentRaf = null;
          const el = document.getElementById('shopify-pc__banner');
          if (el && el !== this.consentEl) {
            this.consentEl = el;
            this.consentElObserver?.disconnect();
            this.consentElObserver = new MutationObserver(this.scheduleConsentCheck);
            this.consentElObserver.observe(el, {
              attributes: true,
              attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
            });
          }
          let open = false;
          if (el) {
            // ATTENTION : ne PAS tester offsetParent. La bannière est en position:fixed, et
            // offsetParent renvoie null pour tout élément fixed (CSSOM View) sur Chrome,
            // Safari et Edge — le test paraissait fonctionner mais était toujours faux,
            // donc la barre n'était jamais masquée. On mesure la boîte et la visibilité.
            const r = el.getBoundingClientRect();
            const cs = getComputedStyle(el);
            open = r.height > 0 && r.width > 0 && cs.visibility !== 'hidden' && cs.display !== 'none';
          }
          if (open !== this.consentOpen) {
            this.consentOpen = open;
            this.render();
          }
        };
        this.consentCheck = check;

        // Les apps tierces injectent régulièrement des enfants de <body>. Sans regroupement,
        // chaque injection déclencherait un getComputedStyle + getBoundingClientRect, donc
        // un reflow synchrone. On regroupe sur la frame suivante.
        this.scheduleConsentCheck = () => {
          if (this.consentRaf) return;
          this.consentRaf = requestAnimationFrame(check);
        };

        this.consentBodyObserver = new MutationObserver(this.scheduleConsentCheck);
        this.consentBodyObserver.observe(document.body, { childList: true });

        this.onConsentCollected = () => {
          this.consentTimer = setTimeout(check, 150);
        };
        document.addEventListener('visitorConsentCollected', this.onConsentCollected);

        check();
      }

      render() {
        const visible = this.scrolledPast && !this.consentOpen;
        this.setAttribute('data-visible', visible ? 'true' : 'false');
      }

      onClick(event) {
        event.preventDefault();

        /* Mode v2 (contrat B13) : la barre ne commande pas, elle ramène au bloc d'achat
           pour que la cliente choisisse sa formule avant de valider. On vise le bloc
           d'information produit, pas le bouton : le bouton seul amènerait la formule
           hors écran, au-dessus du pli. */
        if (this.dataset.mode === 'scroll') {
          /* On vise en priorité le SÉLECTEUR DE FORMULE : la cliente arrive alors sur
             « choisis ta formule → ce qui est inclus → ajouter au panier », les trois
             gestes dans l'ordre. Sans sélecteur, on retombe sur le haut du bloc
             d'achat, puis sur le bouton lui-même. */
          const ancre =
            document.querySelector('[data-ryb-offers]') ||
            document.getElementById(this.dataset.scrollTo) ||
            document.getElementById(this.dataset.target);
          if (!ancre) return;
          /* Le clic remonte TOUJOURS (la barre n'apparaît qu'une fois le bouton dépassé
             par le haut), ce qui fait justement réapparaître l'en-tête collant réglé sur
             « on-scroll-up ». Avec une marge fixe de 24 px, la légende « Choisis ta
             formule » et la première offre finissaient DERRIÈRE l'en-tête. On réserve
             donc sa hauteur réelle. */
          const entete = document.querySelector('sticky-header') || document.querySelector('.section-header');
          const marge = (entete ? entete.offsetHeight : 0) + 16;
          const haut = ancre.getBoundingClientRect().top + window.scrollY - marge;
          window.scrollTo({ top: haut < 0 ? 0 : haut, behavior: 'smooth' });
          return;
        }

        // Si <product-info> a été entièrement remplacé (handleSwapProduct des combined
        // listings), notre référence pointe sur un nœud détaché : on la retrouve par id.
        let target = this.target;
        if (!target || !target.isConnected) {
          target = document.getElementById(this.dataset.target);
          if (!target) return;
          // On repointe TOUS les observateurs, pas seulement l'IntersectionObserver :
          // targetObserver continuait sinon d'écouter un bouton détaché, et la synchro
          // « Épuisé »/disabled cessait silencieusement.
          this.target = target;
          this.io?.disconnect();
          this.io?.observe(target);
          this.targetObserver?.disconnect();
          this.targetObserver = new MutationObserver(this.syncDisabled.bind(this));
          this.targetObserver.observe(target, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
          });
          this.syncDisabled();
        }
        if (target.disabled) return;
        // Relais vers le bouton réel du formulaire produit.
        target.click();
      }

      syncDisabled() {
        const disabled = this.target.disabled === true;
        this.button.disabled = disabled;
        /* En mode v2 le libellé est « Commander » : il ne doit PAS être écrasé par le
           libellé du bouton principal, qui porte désormais le prix de la formule. */
        if (this.dataset.mode === 'scroll') return;
        const label = this.target.querySelector('span');
        if (label && label.textContent.trim()) {
          const texte = label.textContent.trim();
          this.button.textContent = texte;
          // L'aria-label prime TOUJOURS sur le texte visible : laissé figé sur
          // « Ajouter au panier », il faisait annoncer l'inverse de ce qui est affiché
          // dès que la variante passait à « Épuisé ». On le resynchronise.
          this.button.setAttribute('aria-label', `${texte} — ${this.dataset.productTitle || ''}`.trim().replace(/ —$/, ''));
        }
      }

      syncPrice(priceSource) {
        // ATTENTION : dans snippets/price.liquid, le 1er .price-item--regular du DOM est le
        // PRIX COURANT (bloc .price__regular). Le prix barré est le <s class="price-item--regular">
        // du bloc .price__sale. Un querySelector('.price-item--regular') nu renverrait donc
        // le prix courant en guise de prix barré.
        const priceEl = priceSource.querySelector('.price');
        if (!priceEl) return;
        const onSale = priceEl.classList.contains('price--on-sale');
        const current =
          priceSource.querySelector('.price__sale .price-item--sale')?.textContent.trim() ||
          priceSource.querySelector('.price__regular .price-item--regular')?.textContent.trim();
        if (!current) return;
        const compare = priceSource.querySelector('.price__sale s.price-item--regular')?.textContent.trim();

        this.priceSlot.textContent = '';
        const now = document.createElement('span');
        now.className = 'sticky-atc__price';
        now.textContent = current;
        this.priceSlot.appendChild(now);
        if (onSale && compare && compare !== current) {
          const was = document.createElement('s');
          was.className = 'sticky-atc__compare';
          was.textContent = compare;
          this.priceSlot.appendChild(was);
        }
      }
    }
  );
}
